import { useQuery } from "@tanstack/react-query";
import i18n from "@/i18n";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import type { StayShowFilter } from "../schedule/scheduleModel";
import {
  activityMatchesShowFilter,
  activityOccursOn,
  buildNatureMap,
  deriveActivityNature,
  formatRecurrenceLabel,
  isActivityVisibleForCheckedInPets,
  parseActivityRecurrence,
  type ActivityTargetType,
  type StayActivityRecord,
} from "./activitiesModel";

export interface ActivityRow {
  id: string;
  time: string;              // "15h00" / "3:00 PM"
  targetLabel: string;
  targetSub: string;
  targetType: ActivityTargetType;
  targetPetId: string | null;
  targetColor: string | null; // cor da matilha (stay_packs.color); null para pet/all_packs/general
  petInitial: string | null; // avatar de pet (iniciais, como no hi-fi)
  typeName: string;
  typeColor: string;
  typeIcon: string;
  description: string;
  recurrenceLabel: string;   // "" quando não há
  assigneeName: string | null;
  assigneeInitial: string | null;
  done: boolean;
  record: StayActivityRecord; // registro cru, usado para pré-preencher o modal de edição
}

// Linhas cruas do select (scheduled_date não está tipado em types.ts).
interface RawActivity {
  id: string;
  target_type: ActivityTargetType;
  target_pet_id: string | null;
  target_stay_pack_id: string | null;
  scheduled_time: string | null;
  scheduled_date: string | null;
  description: string | null;
  assigned_to: string | null;
  stay_activity_type_id: string;
  stay_activity_types: { name: string; color: string | null; icon: string | null } | null;
  stay_activity_recurrence: {
    frequency: string; interval_days: number | null; days_of_week: number[] | null;
    days_of_month: number[] | null; ends_on: string | null;
  }[] | null;
  target_pet: { name: string } | null;
  target_pack: { name: string; color: string | null } | null;
  stay_activity_completions: { id: string }[] | null;
}

interface ClinicProfessionalRow {
  id: string;
  full_name: string | null;
}

interface ReservationDayRow {
  pet_id: string;
  status: string;
  stay_reservations: {
    module_type: string;
    check_in_date: string;
    check_out_date: string | null;
  } | null;
}

const initials = (name: string | null | undefined) =>
  (name ?? "").trim().slice(0, 1).toUpperCase() || null;

const formatTime = (time: string | null, locale: string): string => {
  if (!time) return "";
  const [h, m] = time.split(":");
  if (locale.startsWith("pt")) return `${h}h${m}`;
  const hour = Number(h);
  const suffix = hour >= 12 ? "PM" : "AM";
  const h12 = ((hour + 11) % 12) + 1;
  return `${h12}:${m} ${suffix}`;
};

export const stayActivitiesKey = (clinicId: string | null, day: string, show: StayShowFilter) =>
  ["stay-activities", clinicId, day, show];

export function useStayActivities({ day, show }: { day: string; show: StayShowFilter }) {
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;
  const locale = i18n.language || "pt-BR";

  return useQuery({
    queryKey: stayActivitiesKey(clinicId, day, show),
    enabled: !!clinicId && !!day,
    queryFn: async (): Promise<ActivityRow[]> => {
      if (!clinicId) return [];

      const activitiesQuery = supabase
        .from("stay_activities")
        .select(`
          id, target_type, target_pet_id, target_stay_pack_id, scheduled_time, scheduled_date,
          description, assigned_to, stay_activity_type_id,
          stay_activity_types ( name, color, icon ),
          stay_activity_recurrence ( frequency, interval_days, days_of_week, days_of_month, ends_on ),
          target_pet:pets!stay_activities_target_pet_id_fkey ( name ),
          target_pack:stay_packs!stay_activities_target_stay_pack_id_fkey ( name, color ),
          stay_activity_completions ( id )
        `)
        .eq("clinic_id", clinicId)
        .eq("is_active", true)
        .eq("stay_activity_completions.completed_date", day);

      const reservationDaysQuery = supabase
        .from("stay_reservation_days")
        .select("pet_id, status, stay_reservations!inner ( module_type, check_in_date, check_out_date )")
        .eq("clinic_id", clinicId)
        .eq("stay_date", day)
        .eq("status", "checked_in");

      // profiles tem RLS restrita à própria linha do usuário; usamos a RPC
      // SECURITY DEFINER (mesma usada para listar profissionais no dialog)
      // para resolver o nome de qualquer responsável da clínica.
      const professionalsQuery = supabase.rpc(
        "get_clinic_professionals" as never,
        { p_clinic_id: clinicId } as never,
      );

      const [
        { data: rawActivities, error: aErr },
        { data: rawReservations, error: rErr },
        { data: rawProfessionals, error: pErr },
      ] = await Promise.all([
        activitiesQuery as unknown as Promise<{ data: RawActivity[] | null; error: unknown }>,
        reservationDaysQuery as unknown as Promise<{ data: ReservationDayRow[] | null; error: unknown }>,
        professionalsQuery as unknown as Promise<{ data: ClinicProfessionalRow[] | null; error: unknown }>,
      ]);
      if (aErr) throw aErr;
      if (rErr) throw rErr;
      if (pErr) throw pErr;

      const assigneeNameById = new Map(
        (rawProfessionals ?? []).map((p) => [p.id, p.full_name]),
      );

      const checkedInPetIds = new Set((rawReservations ?? []).map((row) => row.pet_id));
      const natureMap = buildNatureMap(
        (rawReservations ?? []).flatMap((r) => r.stay_reservations ? [{
          petId: r.pet_id,
          nature: (r.stay_reservations.module_type === "hotel" || r.stay_reservations.module_type === "both" ? r.stay_reservations.module_type : "daycare") as "daycare" | "hotel" | "both",
          checkInDate: r.stay_reservations.check_in_date,
          checkOutDate: r.stay_reservations.check_out_date,
        }] : []),
        day,
      );

      const rows: ActivityRow[] = [];
      for (const a of rawActivities ?? []) {
        const recurrence = parseActivityRecurrence(a.stay_activity_recurrence);
        if (!activityOccursOn(a.scheduled_date, recurrence, day)) continue;
        if (!isActivityVisibleForCheckedInPets(a.target_type, a.target_pet_id, checkedInPetIds)) continue;

        const record: StayActivityRecord = {
          id: a.id, targetType: a.target_type, targetPetId: a.target_pet_id, targetPackId: a.target_stay_pack_id,
          scheduledDate: a.scheduled_date, scheduledTime: a.scheduled_time, description: a.description,
          assignedTo: a.assigned_to, typeId: a.stay_activity_type_id, recurrence,
        };
        const nature = deriveActivityNature(record, natureMap, day);
        if (!activityMatchesShowFilter(nature, show)) continue;

        const type = a.stay_activity_types;
        const targetLabel =
          a.target_type === "pet" ? (a.target_pet?.name ?? "")
          : a.target_type === "pack" ? (a.target_pack?.name ?? "")
          : a.target_type === "all_packs" ? i18n.t("activities.targetAllPacks", { ns: "daycareHotel" })
          : i18n.t("activities.targetGeneral", { ns: "daycareHotel" });
        const targetSub =
          a.target_type === "pack" ? i18n.t("activities.packSub", { ns: "daycareHotel" })
          : a.target_type === "all_packs" ? i18n.t("activities.targetAllPacksSub", { ns: "daycareHotel" })
          : a.target_type === "general" ? i18n.t("activities.targetGeneralSub", { ns: "daycareHotel" })
          : "";

        rows.push({
          id: a.id,
          time: formatTime(a.scheduled_time, locale),
          targetLabel, targetSub, targetType: a.target_type,
          targetPetId: a.target_pet_id,
          targetColor: a.target_type === "pack" ? (a.target_pack?.color ?? null) : null,
          petInitial: a.target_type === "pet" ? initials(a.target_pet?.name) : null,
          typeName: type?.name ?? "",
          typeColor: type?.color ?? "#75716a",
          typeIcon: type?.icon ?? "activity",
          description: a.description ?? "",
          recurrenceLabel: formatRecurrenceLabel(recurrence, locale),
          assigneeName: (a.assigned_to && assigneeNameById.get(a.assigned_to)) ?? null,
          assigneeInitial: initials(a.assigned_to ? assigneeNameById.get(a.assigned_to) : null),
          done: (a.stay_activity_completions ?? []).length > 0,
          record,
        });
      }

      rows.sort((x, y) => (x.time || "~").localeCompare(y.time || "~"));
      return rows;
    },
  });
}
